import {
  CIMSModuleItem,
  CIMSPageItem,
  DeveloperPermissionItem,
  pathify,
} from '@cims-online/shared'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { useAuthContext } from 'context/AuthContext'
import CIMSLink from 'components/CIMSLink'
import { useCallback } from 'react'

/**
 * Creates the developer navigation module for users with full-developer access
 * or specific developer-tool permissions. It deliberately produces only
 * project-independent links because {@link DevSideNav} is outside project routes.
 *
 * Project-side navigation uses a separate hook with project-scoped permissions.
 */
export function useProtectedMenuItem({
  pages,
  icon,
  ...rest
}: Module): ItemType {
  const { user } = useAuthContext()

  const authorized = useCallback(
    (permission: Page['permission']) => {
      if (!user) return false
      else if (permission === 'developer')
        return user.isFullDeveloper().authorized
      return user.someAuthorized([permission]).authorized
    },
    [user]
  )

  if (!user) return null

  /**
   * DevSideNav is mounted only outside project routes, so these links must not
   * depend on a project ID or use the project navigation link component.
   */
  if (pages.every(({ permission }) => !authorized(permission))) return null

  /**
   * Avoid an empty developer submenu, while retaining each developer tool the
   * user is allowed to discover and open.
   */
  return {
    label: rest.module.name,
    key: rest.module.id,
    icon,
    children: pages.reduce((pages, { page, icon, permission }) => {
      if (!authorized(permission)) return pages

      const label = (
        <CIMSLink to={pathify(rest.module.pathname, page.pathname)}>
          {page.name}
        </CIMSLink>
      )
      pages.push({ key: page.id, label, icon })
      return pages
    }, Array<ItemType>()),
  }
}

type Module = {
  module: CIMSModuleItem
  pages: Page[]
  /**
   * @default <FolderOpenOutlined />
   */
  icon?: React.ReactNode
}

type Page = {
  permission: 'developer' | DeveloperPermissionItem
  page: CIMSPageItem
  /**
   * @default null
   */
  icon?: React.ReactNode
}
